/**
 * Runtime Router — COPILOT_MASTER_PLAN subsystem D.
 *
 * Decides where a multi-step goal runs:
 *   • SIMPLE / interactive  → the fast in-browser `runPlan` (agent-runner)
 *   • COMPLEX / cross-module → the durable MAS backend (POST /agentic/mas/plans)
 *
 * "Complex" heuristic (per the master plan): a plan with >3 steps OR that
 * crosses modules routes to MAS; simple plans stay in-browser. This module also
 * owns the MAS-vs-browser decision for a bare GOAL string (before a plan exists)
 * and a small progress-subscription helper the Companion window uses to show a
 * MAS plan's live progress.
 */

import type { AgentPlanStep } from '@/services/copilot.service'
import {
  createMasPlan,
  startMasPlan,
  getMasProgress,
  type MasCreatePlanResult,
  type MasProgress,
} from '@/services/ml-tools.service'

// ── Goal-level heuristic (no plan yet) ────────────────────────────────────────

// Verbs/nouns that signal a multi-module or long-running goal → MAS.
const COMPLEX_HINTS = [
  'puis', 'ensuite', 'et crée', 'et créer', 'et lie', 'et lier', 'et notifie', 'et notifier',
  'chaque', 'tous les', 'toutes les', 'planifie', 'surveille', 'orchestrer', 'orchestre',
  'campagne', 'bout en bout', 'de bout en bout', 'workflow complet', 'multi',
]

// Distinct GRC module keywords — 2+ hits ⇒ the goal crosses modules ⇒ MAS.
const MODULE_KEYWORDS: Record<string, string[]> = {
  risk: ['risque', 'risk', 'kri'],
  compliance: ['conformité', 'conformite', 'obligation', 'contrôle', 'controle', 'cadre', 'framework'],
  audit: ['audit', 'constat', 'finding'],
  governance: ['politique', 'gouvernance', 'policy'],
  security: ['incident', 'sécurité', 'securite', 'vulnérabilité', 'vulnerabilite'],
  vendor: ['fournisseur', 'vendor', 'tiers', 'prestataire'],
  privacy: ['pia', 'aipd', 'rgpd', 'confidentialité', 'consentement'],
  strategy: ['stratégie', 'strategie', 'business case', 'roadmap', 'objectif'],
}

function normalize(t: string): string {
  return t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

/** Count how many distinct GRC modules a goal string touches. */
export function countModulesInGoal(goal: string): number {
  const n = normalize(goal)
  let hits = 0
  for (const kws of Object.values(MODULE_KEYWORDS)) {
    if (kws.some((kw) => n.includes(normalize(kw)))) hits += 1
  }
  return hits
}

/**
 * TRUE when a bare goal string should route to the durable MAS backend rather
 * than the in-browser runner: it either crosses ≥2 modules OR carries a
 * multi-step / long-running hint.
 */
export function createMasPlanForGoal(goal: string): boolean {
  const n = normalize(goal)
  if (countModulesInGoal(goal) >= 2) return true
  return COMPLEX_HINTS.some((h) => n.includes(normalize(h)))
}

// ── Plan-level heuristic (a plan already exists) ──────────────────────────────

// Op names that are the in-browser runner's native, HITL-form-driven writes.
// A plan built ONLY from these (+ reads) is best executed in-browser so the user
// gets the create-form review + HITL diff — never off-loaded to a headless MAS.
const IN_BROWSER_WRITE_OPS = new Set(['fillForm', 'createRecord', 'linkRecords', 'openForm'])

/**
 * Decide the runtime for an already-produced plan.
 *   • >3 steps ⇒ 'mas' (long enough to benefit from durable, parallel agents).
 *   • steps spanning ≥2 DISTINCT modules ⇒ 'mas' (cross-module coordination).
 *   • otherwise ⇒ 'browser' (fast, interactive, in-form HITL).
 * A short single-module create/link plan always stays in-browser so its writes
 * flow through the create-form + HITL diff the user expects.
 */
export function decidePlanRuntime(plan: AgentPlanStep[], planModule?: string): 'browser' | 'mas' {
  if (plan.length === 0) return 'browser'
  if (plan.length > 3) return 'mas'

  // Distinct modules referenced by the STEPS (entity_type/module/type), excluding
  // the ambient page module which shouldn't alone force cross-module routing.
  const stepModules = new Set<string>()
  for (const step of plan) {
    const a = step.args ?? {}
    const m = (a.module ?? a.entity_type ?? a.type) as string | undefined
    if (typeof m === 'string' && m.trim()) stepModules.add(m.trim().toLowerCase())
  }
  if (stepModules.size >= 2) return 'mas'

  // A plan made only of in-browser writes (+ reads) → keep it local so its create
  // form + HITL diff render (even if the page module differs from the step module).
  const allInBrowser = plan.every((s) => IN_BROWSER_WRITE_OPS.has(s.op) || !isWriteish(s))
  if (allInBrowser) return 'browser'

  // Otherwise, if the single step-module differs from the page module, treat as
  // cross-module coordination.
  if (planModule && stepModules.size === 1 && !stepModules.has(planModule.trim().toLowerCase())) return 'mas'
  return 'browser'
}

function isWriteish(step: AgentPlanStep): boolean {
  return (
    step.confirm === true ||
    step.side_effect === 'write' ||
    step.side_effect === 'destructive' ||
    step.side_effect === 'outward'
  )
}

// ── MAS launch + progress subscription ────────────────────────────────────────

export interface MasLaunch {
  ok: boolean
  planId?: string
  create?: MasCreatePlanResult
  /** When MAS is unavailable, the caller should fall back to in-browser runPlan. */
  fellBack?: boolean
  error?: string
}

/**
 * Create + start a MAS plan for a complex goal. When MAS isn't deployed
 * (`missing`), returns `{ ok:false, fellBack:true }` so the Agent panel runs the
 * in-browser plan instead — the goal always executes.
 */
export async function launchMasPlan(input: {
  goal: string
  domain?: string
  autonomy: 'assistant' | 'agent'
  context?: Record<string, unknown>
}): Promise<MasLaunch> {
  const create = await createMasPlan({
    goal: input.goal,
    domain: input.domain,
    autonomy: input.autonomy,
    context: input.context,
    autoStart: true,
  })
  if (create.ok && create.planId) {
    // auto_start dispatches server-side; a defensive explicit start is harmless
    // (idempotent — start returns "already running" when RUNNING).
    if (create.status !== 'RUNNING') {
      await startMasPlan(create.planId)
    }
    return { ok: true, planId: create.planId, create }
  }
  if (create.missing) return { ok: false, fellBack: true, error: 'MAS non déployé' }
  return { ok: false, error: create.error ?? 'Échec de création du plan MAS' }
}

/**
 * Poll a MAS plan's progress until it reaches a terminal state or `signal`
 * aborts. Invokes `onProgress` each tick. Returns the final progress snapshot.
 * Terminal states: DONE, FAILED, CANCELLED (HITL keeps polling — the plan is
 * parked awaiting approval and will resume).
 */
const TERMINAL = new Set(['DONE', 'FAILED', 'CANCELLED', 'COMPLETED', 'SUCCESS', 'ERROR'])

export async function subscribeMasProgress(
  planId: string,
  onProgress: (p: MasProgress) => void,
  opts?: { intervalMs?: number; signal?: AbortSignal; maxTicks?: number },
): Promise<MasProgress | null> {
  const intervalMs = opts?.intervalMs ?? 2500
  const maxTicks = opts?.maxTicks ?? 240 // ~10 min ceiling at 2.5s
  let last: MasProgress | null = null
  for (let i = 0; i < maxTicks; i++) {
    if (opts?.signal?.aborted) break
    const p = await getMasProgress(planId)
    if (p) {
      last = p
      onProgress(p)
      if (TERMINAL.has((p.status ?? '').toUpperCase())) break
    }
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  return last
}
